import { BlockAudiencePanel } from './block-audience-panel.js';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type GetContentResult } from '@whinerp/client';
import { BlockEditor, Button, ErrorState, Icon, Panel, Skeleton } from '@whinerp/ui';
import { BLOCK_PALETTES, type BlockDocument } from '@whinerp/contracts/blocks';
import { useT } from '../../app/i18n.js';
import { blockEditorLabels } from './block-labels.js';
import { useBlockLibrary } from './block-library.js';
import { defineScreen } from '../types.js';

/**
 * SC-18 Email builder.
 *
 * BlockEditor with the email palette. There is no email-specific editor: the
 * palette is the difference, and the preview, the personalisation warning and
 * the block settings are the same component a landing page and a knowledge
 * article use.
 *
 * What this screen adds around it is the autosave and its failure state.
 * 04_Screens asks for a persistent "unsaved" badge with a retry, and the
 * reason is specific: a builder that silently stopped saving looks identical
 * to one that is saving, right up until the tab is closed.
 */

type Content = GetContentResult;

/** The sample the preview personalises against. */
const SAMPLE: Record<string, string> = {
  firstName: 'Priya',
  lastName: 'Raman',
  company: 'Gulf Trading',
};

function EmailBuilderScreen(): JSX.Element {
  const t = useT();
  const library = useBlockLibrary();
  const params = useParams();
  const documentId = params['id'] && params['id'] !== 'new' ? params['id'] : null;

  const [document, setDocument] = useState<BlockDocument | null>(null);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [dirty, setDirty] = useState(false);

  const saved = useQuery<Content, ApiError>({
    queryKey: ['content', documentId],
    queryFn: () => api.getContent({ path: { documentId: documentId ?? '' } }),
    enabled: documentId !== null,
  });

  useEffect(() => {
    if (document !== null) return;
    if (saved.data) {
      setDocument({ blocks: saved.data.document.blocks });
      setName(saved.data.document.name);
      setSubject(saved.data.document.subject ?? '');
      return;
    }
    if (!documentId) setDocument({ blocks: [] });
  }, [saved.data, document, documentId]);

  const save = useMutation({
    mutationFn: () =>
      api.saveContent({
        body: {
          ...(documentId ? { documentId } : {}),
          kind: 'email',
          name: name.trim() || t('content.email'),
          subject: subject.trim() || null,
          blocks: (document ?? { blocks: [] }).blocks,
        },
      }),
    onSuccess: () => setDirty(false),
  });

  if (saved.isError) return <EmailBuilderError error={saved.error} retry={() => void saved.refetch()} />;
  if (!document) return <EmailBuilderLoading />;

  return (
    <div data-component="EmailBuilder" className="flex w-full min-w-0 flex-col gap-3">
      <header className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-dense text-ink">
          {t('campaign.name')}
          <input
            data-content-name
            aria-label={t('campaign.name')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setDirty(true);
            }}
            className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          />
        </label>
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink">
          {t('content.subject')}
          <input
            data-content-subject
            aria-label={t('content.subject')}
            value={subject}
            onChange={(event) => {
              setSubject(event.target.value);
              setDirty(true);
            }}
            className="h-input w-full rounded-control border border-rule bg-surface px-2 text-dense text-ink"
          />
        </label>

        <span className="flex items-center gap-2">
          {/*
           * The save state, said out loud.
           *
           * A builder that stopped saving looks exactly like one that is
           * saving, until the tab closes. So the badge is persistent and
           * carries its own retry rather than a toast that has already gone.
           */}
          {save.isError ? (
            <span
              role="alert"
              data-save-failed
              className="flex items-center gap-2 rounded-card bg-danger-tint px-2 py-1 text-meta text-danger-text"
            >
              <Icon name="Warning" size="inline" decorative />
              {t('content.unsaved')}
              <Button intent="ghost" size="small" data-retry-save onClick={() => save.mutate()}>
                {t('content.retry')}
              </Button>
            </span>
          ) : dirty ? (
            <span data-unsaved className="text-meta text-warning-text">
              {t('content.unsaved')}
            </span>
          ) : save.isSuccess ? (
            <span data-saved className="text-meta text-ink-meta">
              {t('content.saved')}
            </span>
          ) : null}
          <Button intent="primary" icon="FloppyDisk" data-save-content busy={save.isPending} onClick={() => save.mutate()}>
            {t('content.save')}
          </Button>
        </span>
      </header>

      <Panel>
        <BlockEditor
          data-email-editor
          palette={BLOCK_PALETTES.email}
          value={document}
          onChange={(next) => {
            setDocument(next);
            setDirty(true);
          }}
          sample={SAMPLE}
          library={library}
          labels={blockEditorLabels(t)}
          /* A test send exists here and not on a landing page, because an
             email is the only one of the three that sends. */
          onTestSend={() => save.mutate()}
        />
      </Panel>
      {/* Who sees each block (G-178). */}
      <BlockAudiencePanel
        document={document}
        onChange={(next) => {
          setDocument(next);
          setDirty(true);
        }}
      />
    </div>
  );
}

function EmailBuilderEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('content.empty')}</p>;
}

function EmailBuilderLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      {/* Block outlines preserved, so the canvas does not jump when it lands. */}
      <Skeleton className="h-plot w-panel-narrow" />
      <Skeleton className="h-plot flex-1" />
      <Skeleton className="h-plot w-panel-wide" />
    </div>
  );
}

function EmailBuilderError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This email could not be loaded"
      body={error instanceof Error ? error.message : 'The content did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-18',
  route: '/emails/:id/edit',
  surface: 'page',
  title: 'Email builder',
  features: ['F-064'],
  entitlement: 'F-064',
  reachedBy: [{ kind: 'screen', from: 'SC-17', how: 'the content link on a campaign' }],
  components: ['BlockEditor'],
  elements: ['EmailBuilder'],
  sampleParams: { id: 'new' },
  states: { empty: EmailBuilderEmpty, loading: EmailBuilderLoading, error: EmailBuilderError },
  component: EmailBuilderScreen,
});

export default screen;
